/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */
import { ChangeTypeInternal, } from './persisted-types';
import { copyPropertyIfDefined, fail } from './Common';
import { convertStablePlaceIds, convertStableRangeIds } from './IdConversion';
/**
 * Encapsulates knowledge of how to compress/decompress a change into a compressed change
 * Compression and decompression each take in a {@link StringInterner} for deduplicating shared strings.
 * @internal
 */
export class ChangeCompressor {
    constructor(treeCompressor) {
        this.treeCompressor = treeCompressor;
    }
    compress(change, interner, idNormalizer) {
        if (change.type === ChangeTypeInternal.Build) {
            const source = [];
            for (const node of change.source) {
                source.push(this.treeCompressor.compress(node, interner, idNormalizer));
            }
            const newChange = {
                destination: change.destination,
                source,
                type: ChangeTypeInternal.CompressedBuild,
            };
            return newChange;
        }
        else {
            return normalizeChange(change, (id) => idNormalizer.normalizeToOpSpace(id));
        }
    }
    decompress(change, interner, idNormalizer) {
        if (change.type === ChangeTypeInternal.CompressedBuild) {
            const source = [];
            for (const node of change.source) {
                source.push(this.treeCompressor.decompress(node, interner, idNormalizer));
            }
            const newChange = {
                destination: change.destination,
                source,
                type: ChangeTypeInternal.Build,
            };
            return newChange;
        }
        else {
            return normalizeChange(change, (id) => idNormalizer.normalizeToSessionSpace(id));
        }
    }
}
function normalizeChange(change, normalizeId) {
    switch (change.type) {
        case ChangeTypeInternal.Insert: {
            const insert = {
                source: change.source,
                destination: convertStablePlaceIds(change.destination, normalizeId),
                type: ChangeTypeInternal.Insert,
            };
            return insert;
        }
        case ChangeTypeInternal.Detach: {
            const detach = {
                source: convertStableRangeIds(change.source, normalizeId),
                type: ChangeTypeInternal.Detach,
            };
            copyPropertyIfDefined(change, detach, 'destination');
            return detach;
        }
        case ChangeTypeInternal.SetValue: {
            const setValue = {
                nodeToModify: normalizeId(change.nodeToModify),
                payload: change.payload,
                type: ChangeTypeInternal.SetValue,
            };
            return setValue;
        }
        case ChangeTypeInternal.Constraint: {
            const constraint = {
                effect: change.effect,
                toConstrain: convertStableRangeIds(change.toConstrain, normalizeId),
                type: ChangeTypeInternal.Constraint,
            };
            copyPropertyIfDefined(change, constraint, 'contentHash');
            copyPropertyIfDefined(change, constraint, 'identityHash');
            copyPropertyIfDefined(change, constraint, 'label');
            copyPropertyIfDefined(change, constraint, 'length');
            if (change.parentNode !== undefined) {
                constraint.parentNode = normalizeId(change.parentNode);
            }
            return constraint;
        }
        default:
            fail('unexpected change type');
    }
}
/**
 * Compresses the provided edit by applying `compressor` to each change and leaving other fields
 * untouched.
 */
export function compressEdit(compressor, interner, idNormalizer, edit) {
    return Object.assign(Object.assign({}, edit), { changes: edit.changes.map((change) => compressor.compress(change, interner, idNormalizer)) });
}
/**
 * Decompresses the provided edit by applying `compressor` to each change and leaving other fields
 * untouched.
 */
export function decompressEdit(compressor, interner, idNormalizer, edit) {
    return Object.assign(Object.assign({}, edit), { changes: edit.changes.map((change) => compressor.decompress(change, interner, idNormalizer)) });
}
//# sourceMappingURL=ChangeCompression.js.map